/* eslint-disable max-len */
/**
 * Static fallback answers used when the Ask AI call fails or comes back empty.
 *
 * Keyed by the form `fieldName` (question key) of the field the Ask AI button
 * belongs to. Add an entry here to give a question a canned answer instead of
 * silently resetting the button back to its initial state.
 */
export const askAIFallbackAnswers: Record<string, string> = {
  pampQuestion6: `The national and international codes, certifications, labels, and standards adopted in the year 2024 are outlined in the Business Responsibility and Sustainability Report (BRSR). The policies cover all principles from P1 to P9, which include:

* P1: Conducting business with integrity, ethics, transparency, and accountability.
* P2: Providing goods and services sustainably and safely.
* P3: Respecting and promoting employee well-being.
* P4: Being responsive to stakeholder interests.
* P5: Respecting and promoting human rights.
* P6: Protecting and restoring the environment.
* P7: Engaging in public policy influence responsibly and transparently.
* P8: Promoting inclusive growth and equitable development.
* P9: Engaging with consumers responsibly.

Additionally, affiliations with several trade and industry chambers/associations, including the Hotel Association of India, CII - Confederation of Indian Industry, Indo-German Chamber of Commerce, and Federation of Indian Export Organisation, reflect a commitment to engaging with industry bodies to promote responsible and transparent business practices.`,

  pampQuestion7: `In the year 2024, the specific commitments, goals, and targets set are as follows:

1. Health and Safety: Achieve 100% assessment of plants and offices by entities, statutory authorities, or third parties to ensure adherence to health and safety practices and working conditions.
2. Research & Development (R&D): There is no direct input towards R&D for the fiscal year 2023-24.
3. Capital Expenditure (CAPEX): Increase investments in energy-saving initiatives, including the installation of bottling plants, Variable Energy Drives (VED), heat pumps, and LED bulbs, with a CAPEX allocation of 12.54%, up from 5.21% in the previous fiscal year.
4. Policy and Management Processes: Ensure that policies cover all principles (P1 to P9), focusing on conducting business with integrity, providing sustainable goods and services, and promoting human rights.
5. Employee and Worker Development: Maintain a high percentage of career development reviews, with 95% for permanent employees and 100% for permanent workers in the fiscal year 2023-24.

These commitments underscore a focus on compliance, continuous improvement in employee health and safety, sustainable practices, and operational efficiency.`,

  pampQuestion8: `In the year 2024, the performance against specific commitments, goals, and targets is as follows:

1. Research & Development (R&D) and Capital Expenditure (CAPEX): There was no investment in R&D for FY 2023-24. CAPEX investments increased to 12.54% in FY 2023-24, with a focus on energy-saving initiatives such as bottling plants, Variable Energy Drives (VED), heat pumps, and LED bulbs.
2. Employee Career Development: Career reviews were conducted for 95% of permanent employees and 100% of permanent workers in FY 2023-24.
3. Policy and Management Processes: Policies cover all principles related to business integrity, sustainability, employee well-being, stakeholder interests, human rights, environmental protection, public policy, inclusive growth, and consumer engagement.
4. Related Party Transactions (RPTs): Purchases with related parties increased slightly to 19.81%, sales to related parties decreased slightly to 1.55%, and investments in related parties increased to 97.33% in FY 2023-24.
5. Energy Consumption Targets: At the Taj Coromandel location, the Specific Energy Consumption (SEC) target under the PAT Scheme was achieved, with an actual SEC of 47.96 TOE per 1000 m against a target of 48.66 TOE per 1000 m.

The entity has demonstrated a strong commitment to sustainable practices and operational efficiency, particularly in energy-saving measures and comprehensive policy coverage.`,

  glaoQuestion1: `The statement by the director responsible for the business responsibility report in the year 2024 highlights the following ESG-related challenges, targets, and achievements:

1. Policy Framework: The report confirms that all principles (P1 to P9) are comprehensively covered by the company's policies, ensuring a robust policy framework.
2. Principles:

* Conducting business with integrity, ethics, transparency, and accountability.
* Providing goods and services sustainably and safely.
* Respecting and promoting employee well-being.
* Being responsive to stakeholder interests.
* Respecting and promoting human rights.
* Protecting and restoring the environment.
* Engaging in public policy influence responsibly and transparently.
* Promoting inclusive growth and equitable development.
* Engaging with consumers responsibly.

1. Achievements:

* All plants and offices were assessed for health and safety practices and working conditions by entities, statutory authorities, or third parties, demonstrating a commitment to compliance and continuous improvement in employee health and safety.

1. Board Diversity: Women hold 20% of the positions in the Board of Directors and 33.33% in Key Management Personnel, reflecting a focus on diversity in leadership roles.`,

  glaoQuestion2:
    'The highest authorities responsible for the implementation and oversight of the Business Responsibility policies in the year 2024 are the Corporate Social Responsibility committee and the Sustainability committee within the Board of Directors. Additionally, an ESG Task Force in the management team works closely on all future Sustainability and ESG initiatives.'
};

/**
 * Resolves the fallback lookup key from a form field name, which may be a plain
 * string or a nested path such as ['pampQuestion6', 0, 'value'].
 */
export const getAskAIFallback = (
  fieldName: string | Array<string | number> | undefined
): string | undefined => {
  if (!fieldName) {
    return undefined;
  }

  if (typeof fieldName === 'string') {
    return askAIFallbackAnswers[fieldName];
  }

  const match = [...fieldName]
    .reverse()
    .find(
      (part) =>
        typeof part === 'string' && askAIFallbackAnswers[part] !== undefined
    );

  return typeof match === 'string' ? askAIFallbackAnswers[match] : undefined;
};
